import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Flame, Target, CheckSquare, Bell } from "lucide-react";
import FocusSection from "@/components/dashboard/FocusSection";
import QuickWinsSection from "@/components/dashboard/QuickWinsSection";
import WeeklyProgress from "@/components/dashboard/WeeklyProgress";
import NeedsAttention from "@/components/dashboard/NeedsAttention";
import ProjectsGrid from "@/components/dashboard/ProjectsGrid";
import GoalProgressWidget from "@/components/dashboard/GoalProgressWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GamificationBar({ token }) {
  const [gam, setGam] = useState(null);
  const [reminders, setReminders] = useState({ total_incomplete: 0, reminders: [] });
  const [reminderOpen, setReminderOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const bellRef = useRef(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [gamRes, remRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/gamification/overview`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BACKEND_URL}/api/reminders/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setGam(gamRes.data);
        setReminders(remRes.data);
      } catch (err) { console.error("fetchAll (gamification/reminders) failed:", err); }
    };
    fetchAll();
  }, [token]);

  const handleBellClick = () => {
    if (!reminderOpen && bellRef.current) {
      const r = bellRef.current.getBoundingClientRect();
      const dropW = Math.min(280, window.innerWidth - 16);
      const rightVal = Math.max(8, window.innerWidth - r.right);
      // Clamp so dropdown doesn't overflow the left edge
      const clampedRight = Math.min(rightVal, window.innerWidth - dropW - 8);
      setDropPos({
        top: r.bottom + 8,
        right: Math.max(8, clampedRight),
      });
    }
    setReminderOpen((v) => !v);
  };

  if (!gam) return null;

  const pill = (icon, label, value) => (
    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
      <span style={{ color: "#3d6b1f", display: "flex" }}>{icon}</span>
      <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.8rem", fontWeight: 600 }}>
        {value}
      </span>
      <span style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.75rem" }}>
        {label}
      </span>
    </div>
  );

  const divider = (
    <div style={{ width: "1px", height: "16px", background: "rgba(26,77,46,0.15)", flexShrink: 0 }} />
  );

  return (
    <div
      data-testid="gamification-bar"
      style={{
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.6)",
        borderRadius: "0.75rem",
        padding: "0.6rem 1.1rem",
        display: "flex", alignItems: "center",
        gap: "0.75rem", flexWrap: "wrap",
        position: "relative",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {/* Level + XP bar */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <div style={{
          background: "rgba(26,77,46,0.1)", border: "1.5px solid rgba(26,77,46,0.25)",
          borderRadius: "2rem", padding: "0.15rem 0.65rem",
          fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontWeight: 800, fontSize: "0.75rem",
        }}>
          Lv.{gam.level}
        </div>
        <div style={{ width: "80px", height: "6px", background: "rgba(26,77,46,0.12)", borderRadius: "3px", overflow: "hidden" }}>
          <div style={{
            height: "100%", width: `${gam.level_progress_pct}%`,
            background: "linear-gradient(90deg, #1A4D2E, #86c559)", borderRadius: "3px",
          }} />
        </div>
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.7rem" }}>
          {gam.xp_in_level}/{gam.xp_needed} XP
        </span>
      </div>

      {divider}
      {gam.current_streak > 0 && pill(<Flame size={13} />, "streak", `${gam.current_streak}d`)}
      {gam.current_streak > 0 && divider}
      {pill(<Target size={13} />, "completion", `${gam.field_goal_pct}%`)}
      {gam.total_tasks_completed > 0 && (
        <>
          {divider}
          {pill(<CheckSquare size={13} />, "tasks done", gam.total_tasks_completed)}
        </>
      )}

      {/* ── Reminders bell ── */}
      {reminders.total_incomplete > 0 && (
        <>
          {divider}
          <div style={{ position: "relative" }}>
            <button
              ref={bellRef}
              data-testid="reminders-bell-btn"
              onClick={handleBellClick}
              style={{
                display: "flex", alignItems: "center", gap: "0.35rem",
                background: reminderOpen ? "rgba(26,77,46,0.12)" : "transparent",
                border: "none", cursor: "pointer", padding: "0.2rem 0.4rem",
                borderRadius: "0.4rem", transition: "background 0.15s",
              }}
            >
              <span style={{ color: "#d97706", display: "flex" }}><Bell size={13} /></span>
              <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.8rem", fontWeight: 600 }}>
                {reminders.total_incomplete}
              </span>
              <span style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.75rem" }}>
                reminder{reminders.total_incomplete !== 1 ? "s" : ""}
              </span>
            </button>
          </div>

          {/* Portal dropdown — fixed position, never causes layout overflow */}
          {reminderOpen && ReactDOM.createPortal(
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 998 }} onClick={() => setReminderOpen(false)} />
              <div
                data-testid="reminders-dropdown"
                style={{
                  position: "fixed",
                  top: dropPos.top,
                  right: dropPos.right,
                  width: "min(280px, calc(100vw - 1rem))",
                  zIndex: 999,
                  background: "rgba(255,255,255,0.97)",
                  backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.8)",
                  borderRadius: "0.75rem",
                  boxShadow: "0 8px 32px rgba(26,77,46,0.14)",
                  padding: "0.75rem",
                  maxHeight: "min(320px, 60vh)", overflowY: "auto",
                }}
              >
                <p style={{
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.65rem", fontWeight: 700,
                  color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em",
                  margin: "0 0 0.5rem",
                }}>
                  Pending Reminders
                </p>
                {reminders.reminders.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      padding: "0.5rem 0.6rem", borderRadius: "0.5rem",
                      background: "rgba(26,77,46,0.04)", marginBottom: "0.35rem",
                      border: "1px solid rgba(26,77,46,0.08)",
                    }}
                  >
                    <p style={{
                      margin: 0, fontSize: "0.8rem", color: "#1A4D2E",
                      fontWeight: 500, fontFamily: "'Manrope', sans-serif", lineHeight: 1.4,
                    }}>
                      {r.text}
                    </p>
                    <p style={{
                      margin: "0.2rem 0 0", fontSize: "0.7rem", color: "#9ca3af",
                      fontFamily: "'Manrope', sans-serif",
                    }}>
                      {r.project_emoji} {r.project_title}
                    </p>
                  </div>
                ))}
              </div>
            </>,
            document.body
          )}
        </>
      )}
    </div>
  );
}

export default function DashboardHome({
  isMobile,
  onOpenKanban,
  onOpenSettings,
  onTaskComplete,
  onCreateProject,
  refreshKey,
  onGamificationData,
  onNavigate,
}) {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch (err) { console.error("fetchStats failed:", err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats, refreshKey]);

  const handleQuickWinComplete = useCallback(async (taskId) => {
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/tasks/${taskId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onGamificationData) onGamificationData(data);
      fetchStats();
      if (onTaskComplete) onTaskComplete();
    } catch (err) { console.error("quick win complete failed:", err); }
  }, [token, fetchStats, onTaskComplete, onGamificationData]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });

  if (loading) {
    return (
      <div style={{ width: "100%", maxWidth: "1100px", padding: "3rem 0", textAlign: "center" }}>
        <p style={{ fontFamily: "'Manrope', sans-serif", color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
          Loading dashboard...
        </p>
      </div>
    );
  }

  const twoCol = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: "1rem",
  };

  return (
    <div style={{ width: "100%", maxWidth: "1100px", display: "flex", flexDirection: "column", gap: "1rem", boxSizing: "border-box" }}>

      {/* ── Greeting header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-end", flexWrap: "wrap", gap: "0.5rem",
      }}>
        <div>
          <h1 style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: isMobile ? "1.5rem" : "1.75rem",
            fontWeight: 800, color: "#1A4D2E", margin: 0,
            textShadow: "0 1px 3px rgba(255,255,255,0.9), 0 2px 8px rgba(255,255,255,0.7)",
          }}>
            {greeting}, {user?.name}
          </h1>
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            color: "#374151", fontSize: "0.875rem", margin: "0.2rem 0 0",
          }}>
            {dateStr}
          </p>
        </div>
        {stats?.streak > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: "0.4rem",
            background: "rgba(255, 190, 0, 0.2)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255,190,0,0.4)", borderRadius: "2rem",
            padding: "0.4rem 0.9rem",
          }}>
            <span style={{ fontSize: "1rem" }}>🔥</span>
            <span style={{
              fontFamily: "'Manrope', sans-serif", fontWeight: 700,
              color: "#92400e", fontSize: "0.875rem",
            }}>
              {stats.streak} day streak
            </span>
          </div>
        )}
      </div>

      {/* ── Gamification bar ── */}
      <GamificationBar token={token} />

      {/* ── Focus + Quick Wins ── */}
      <div style={twoCol}>
        <FocusSection
          tasks={stats?.focus_tasks || []}
          wipCount={stats?.wip_count || 0}
          projects={stats?.projects || []}
          onOpenKanban={onOpenKanban}
        />
        <QuickWinsSection
          tasks={stats?.quick_wins || []}
          inboxTotal={stats?.inbox_total || 0}
          onComplete={handleQuickWinComplete}
        />
      </div>

      {/* ── Goal Progress Widget ── */}
      <GoalProgressWidget token={token} onNavigate={onNavigate || (() => {})} />

      {/* ── Weekly Progress + Needs Attention ── */}
      <div style={twoCol}>
        <WeeklyProgress stats={stats?.weekly_stats} />
        <NeedsAttention data={stats?.needs_attention} token={token} />
      </div>

      {/* ── Projects Grid ── */}
      <ProjectsGrid
        projects={stats?.projects || []}
        onOpenKanban={onOpenKanban}
        onSettings={onOpenSettings}
        onCreateProject={onCreateProject}
      />
    </div>
  );
}
